import { createContext, useContext, type ReactNode } from 'react'
import { defaultPlatformConfig, type PlatformAdapter } from '../types/adapter'

const PlatformContext = createContext<PlatformAdapter | null>(null)

export interface PlatformProviderProps {
  adapter: PlatformAdapter
  children: ReactNode
}

/**
 * Provider component that makes the platform adapter available to all child components
 */
export function PlatformProvider({ adapter, children }: PlatformProviderProps) {
  return <PlatformContext.Provider value={adapter}>{children}</PlatformContext.Provider>
}

/**
 * Hook to access the platform adapter
 * @throws Error if used outside PlatformProvider
 */
export function usePlatform(): PlatformAdapter {
  const context = useContext(PlatformContext)
  if (!context) {
    throw new Error('usePlatform must be used within a PlatformProvider')
  }
  return context
}

/**
 * Hook to access only the storage adapter
 */
export function useStorage() {
  const { storage } = usePlatform()
  return storage
}

/**
 * Hook to access only the auth adapter
 */
export function useAuth() {
  const { auth } = usePlatform()
  return auth
}

/**
 * Hook to access platform configuration
 */
export function usePlatformConfig() {
  const context = useContext(PlatformContext)
  return context?.config ?? defaultPlatformConfig
}
